import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import { authState, authActions } from '../../stores/authStore';
import { adminStore } from '../../stores/adminStore';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

// How long the "you're about to be signed out" warning shows before logout.
const WARNING_MS = 60_000;
// Fallback if the per-company setting hasn't loaded yet (matches the store default).
const DEFAULT_IDLE_MIN = 60;
// sessionStorage flag so the login page can explain why the user was bumped.
export const IDLE_LOGOUT_FLAG = 'arm.idleLogout';

/**
 * Signs the user out after a period of inactivity. The timeout is the
 * per-company `idleTimeoutMinutes` admin setting (0 disables it; default 60).
 * Mounted only on authenticated routes (see routes/__root.tsx).
 */
export function IdleLogoutGuard() {
  const { user } = useSnapshot(authState);
  const { idleTimeoutMinutes } = useSnapshot(adminStore);
  const navigate = useNavigate();

  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(WARNING_MS / 1000));

  const timeoutMin =
    typeof idleTimeoutMinutes === 'number' && Number.isFinite(idleTimeoutMinutes)
      ? Math.max(0, idleTimeoutMinutes)
      : DEFAULT_IDLE_MIN;

  // 0 disables the timer; only run when signed in.
  const enabled = Boolean(user) && timeoutMin > 0;

  const { reset } = useIdleTimeout({
    enabled,
    timeoutMs: timeoutMin * 60 * 1000,
    warningMs: WARNING_MS,
    onWarn: () => setWarning(true),
    onTimeout: async () => {
      setWarning(false);
      try {
        sessionStorage.setItem(IDLE_LOGOUT_FLAG, '1');
      } catch {
        // sessionStorage unavailable — proceed without the banner.
      }
      await authActions.logout();
      navigate({ to: '/login' });
    },
  });

  // Live countdown while the warning dialog is open.
  useEffect(() => {
    if (!warning) return;
    setSecondsLeft(Math.ceil(WARNING_MS / 1000));
    const tick = window.setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(tick);
  }, [warning]);

  const stayIn = () => {
    setWarning(false);
    reset();
  };

  if (!warning) return null;

  return (
    <Modal
      isOpen={warning}
      onClose={stayIn}
      title="Still there?"
      subtitle="You're about to be signed out"
      size="sm"
      gradient="from-amber-500 to-orange-600"
    >
      <div className="p-6">
        <p className="text-gray-700">
          You'll be signed out for inactivity in{' '}
          <strong>{secondsLeft}</strong> {secondsLeft === 1 ? 'second' : 'seconds'}.
        </p>
        <div className="mt-6 flex justify-end">
          <Button onClick={stayIn}>Stay signed in</Button>
        </div>
      </div>
    </Modal>
  );
}
